import React, { useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const ESTADOS = ["AC","AL","AM","AP","BA","CE","DF","ES","GO","MA","MG","MS","MT","PA","PB","PE","PI","PR","RJ","RN","RO","RR","RS","SC","SE","SP","TO"];

const NIVEL_COLOR = {
  ok: { border: "border-[#00A859]", bg: "bg-[#F0FAF5]", text: "text-[#00A859]" },
  atencao: { border: "border-yellow-500", bg: "bg-yellow-50", text: "text-yellow-700" },
  critico: { border: "border-[#E60000]", bg: "bg-red-50", text: "text-[#E60000]" },
};

export default function AgregadoMontadores() {
  const [filtros, setFiltros] = useState({ estado: "", cidade: "", bairro: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const buscar = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.entries(filtros).filter(([_, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
      const r = await api.get(`/empresa/agregado-montadores-por-regiao${params ? "?" + params : ""}`);
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [filtros]);

  const cor = data ? (NIVEL_COLOR[data.nivel] || NIVEL_COLOR.ok) : NIVEL_COLOR.ok;

  return (
    <div className="p-8">
      <div className="mb-6">
        <div className="label-cap">Comercial · MontaJá</div>
        <h1 className="font-heading font-extrabold text-3xl tracking-tighter mt-1">Agregado de Montadores por Região</h1>
        <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
          Compare capacidade da rede vs demanda da sua operação por região.
          Identifique déficits antes que eles virem atrasos. Sem expor identidade individual.
        </p>
      </div>

      {/* Filtros */}
      <div className="bg-white border border-border p-5 mb-6 grid md:grid-cols-4 gap-3 items-end">
        <div>
          <Label className="label-cap">Estado</Label>
          <Select value={filtros.estado} onValueChange={(v) => setFiltros(f => ({ ...f, estado: v === "_todos" ? "" : v }))}>
            <SelectTrigger className="mt-2" data-testid="agr-estado"><SelectValue placeholder="Todos" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_todos">Todos</SelectItem>
              {ESTADOS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <FF label="Cidade" v={filtros.cidade} on={v => setFiltros(f => ({...f, cidade: v}))} testid="agr-cidade" />
        <FF label="Bairro" v={filtros.bairro} on={v => setFiltros(f => ({...f, bairro: v}))} testid="agr-bairro" />
        <Button onClick={buscar} disabled={loading} className="btn-sharp brand-bg text-white" data-testid="agr-buscar">
          {loading ? "Buscando…" : "Analisar região"}
        </Button>
      </div>

      {!data ? (
        <div className="border border-dashed border-border bg-white p-16 text-center text-muted-foreground">
          Selecione uma região e clique em <strong>Analisar região</strong> para ver capacidade vs demanda.
        </div>
      ) : (
        <>
          {/* Recomendação */}
          <div className={`border-l-4 ${cor.border} ${cor.bg} p-5 mb-6`} data-testid={`agr-rec-${data.nivel}`}>
            <div className={`label-cap ${cor.text}`}>Recomendação</div>
            <div className="font-heading font-bold text-lg mt-1">{data.recomendacao}</div>
            <div className="text-xs text-muted-foreground mt-1">
              {data.filtros.cidade || "Todas as cidades"}
              {data.filtros.estado && `/${data.filtros.estado}`}
              {data.filtros.bairro && ` · ${data.filtros.bairro}`}
            </div>
          </div>

          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border border border-border mb-6">
            <KPI label="Montadores total" v={data.montadores_total} />
            <KPI label="Ativos" v={data.montadores_ativos} highlight />
            <KPI label="Disponíveis" v={data.montadores_disponiveis} />
            <KPI label="Score médio" v={data.score_medio_regiao?.toFixed(0) || "—"} />
          </div>

          {/* Capacidade vs Demanda */}
          <div className="grid md:grid-cols-2 gap-px bg-border border border-border mb-6">
            <div className="bg-white p-6">
              <div className="label-cap">Capacidade semanal</div>
              <div className="font-heading font-extrabold text-4xl mt-2" data-testid="agr-cap">{data.capacidade_semanal}</div>
              <div className="text-xs text-muted-foreground mt-1">atendimentos/semana</div>
            </div>
            <div className="bg-white p-6">
              <div className="label-cap">Demanda semanal</div>
              <div className="font-heading font-extrabold text-4xl mt-2" data-testid="agr-dem">{data.demanda_semanal}</div>
              <div className="text-xs text-muted-foreground mt-1">{data.demanda_mensal}/mês (volumetria cadastrada)</div>
            </div>
          </div>

          {/* Barra de cobertura */}
          <div className="bg-white border border-border p-6 mb-6">
            <div className="flex justify-between items-end mb-2">
              <div className="label-cap">Cobertura da região</div>
              <div className={`font-heading font-extrabold text-2xl ${cor.text}`} data-testid="agr-cob">
                {data.cobertura_pct}%
              </div>
            </div>
            <div className="h-3 bg-secondary rounded-none overflow-hidden">
              <div className={`h-full ${data.nivel === "ok" ? "bg-[#00A859]" : data.nivel === "atencao" ? "bg-yellow-500" : "bg-[#E60000]"}`}
                style={{ width: `${Math.min(100, data.cobertura_pct)}%` }} />
            </div>
            <div className="grid grid-cols-2 mt-3 text-xs">
              <div>
                <div className="label-cap">Déficit semanal</div>
                <div className="font-mono font-bold text-[#E60000]" data-testid="agr-def">{data.deficit_semanal} atend.</div>
              </div>
              <div className="text-right">
                <div className="label-cap">Superávit semanal</div>
                <div className="font-mono font-bold text-[#00A859]">{data.superavit_semanal} atend.</div>
              </div>
            </div>
          </div>

          {data.montadores_recomendados > 0 && (
            <div className="bg-[#142766] text-white p-6" data-testid="agr-acao">
              <div className="label-cap text-white/70">Ação recomendada</div>
              <div className="font-heading font-extrabold text-2xl mt-2">
                Cadastrar mais {data.montadores_recomendados} montador(es) nesta região
              </div>
              <p className="text-xs text-white/80 mt-2">
                Cada montador médio cobre ~15 atendimentos/semana. Use os links de convite para crescer a rede.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function FF({ label, v, on, testid }) {
  return (
    <div>
      <Label className="label-cap">{label}</Label>
      <Input value={v || ""} onChange={(e) => on(e.target.value)} className="mt-2" data-testid={testid} />
    </div>
  );
}

function KPI({ label, v, highlight }) {
  return (
    <div className={`bg-white p-5 ${highlight ? "border-l-4 border-l-[#142766]" : ""}`}>
      <div className="label-cap">{label}</div>
      <div className="font-heading font-extrabold text-3xl mt-2">{v}</div>
    </div>
  );
}
